"use client";

import { useState } from "react";
import {
  Car,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  Pin,
} from "lucide-react";
import clsx from "clsx";
import Link from "next/link";
import { useAuth } from "@/store/auth";
import { allModules, commonModules, ModuleKey } from "@/lib/moduleConfig";
import { iconsMap } from "@/lib/icons";
import { usePathname } from "next/navigation";
import { useSidebarToggle } from "@/store/common";

export default function Sidebar() {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [activeMenus, setActiveMenus] = useState<string[]>([]);
  const pathname = usePathname();
  const { allowedModules } = useAuth();
  const isLoggedIn = useAuth((s) => s.isLoggedIn);
  const [isFixed, setIsFixed] = useState(false);

  const { setSidebarData, SidebarData } = useSidebarToggle();

  // if (!isLoggedIn) return null;

  const toggleMenu = (label: string) => {
    setActiveMenus((prev) =>
      prev.includes(label)
        ? prev.filter((item) => item !== label)
        : [...prev, label]
    );
  };

  function isModuleSelected(
    mod: (typeof allModules)[number],
    pathname: string
  ): boolean {
    if (
      mod.path &&
      (pathname === mod.path || pathname.startsWith(mod.path + "/"))
    )
      return true;

    if (mod.children) {
      return mod.children.some((child) => isModuleSelected(child, pathname));
    }

    return false;
  }

  const isActive = (label: string) => activeMenus.includes(label);

  const toggleMobile = () => setIsMobileOpen((v) => !v);
  const toggleCollapse = () => {
    setIsCollapsed((v) => !v);
    setSidebarData({ isCollapsed: !isCollapsed });
  };

  const mergedModules = Array.from(
    new Set([...(allowedModules || []), ...commonModules])
  );

  const filteredModules = allModules.filter((mod) =>
    mergedModules.includes(mod.key)
  );

  const renderModules = (modules: typeof allModules, level = 0) => {
    return modules.map((mod) => {
      const key = mod.key as ModuleKey;
      const Icon = iconsMap[key] ?? Car;
      const isExpanded = isActive(mod.label);
      const hasChildren = mod.children && mod.children.length > 0;
      const isSelected = isModuleSelected(mod, pathname);

      return (
        <div key={mod.key}>
          {hasChildren ? (
            <button
              type="button"
              className={clsx(
                "flex items-center text-sm justify-between w-full py-2 text-left hover:bg-gray-800 text-gray-300 rounded transition-all overflow-hidden",
                {
                  "justify-center": isCollapsed,
                  [`pl-${Math.min(level * 4 + 3, 12)}`]: !isCollapsed,
                }
              )}
              onClick={() => toggleMenu(mod.label)}
              aria-expanded={isExpanded}
              aria-controls={`${mod.key}-submenu`}
            >
              <div className="flex items-center gap-2">
                <Icon size={18} />
                {!isCollapsed && <span>{mod.label}</span>}
              </div>
              {!isCollapsed &&
                (isExpanded ? (
                  <ChevronUp size={16} />
                ) : (
                  <ChevronDown size={16} />
                ))}
            </button>
          ) : (
            <Link
              href={mod.path ?? "#"}
              className={clsx(
                `flex items-center text-sm w-full p-2 text-left text-gray-300 hover:${
                  !isSelected ? "bg-gray-700" : ""
                } rounded transition-all`,
                {
                  "justify-center": isCollapsed,
                  [`pl-${Math.min(level * 4 + 3, 12)}`]: !isCollapsed,
                  "bg-[#55BD42] text-white": isSelected,
                }
              )}
              onClick={() => setIsMobileOpen(false)}
            >
              <Icon size={18} />
              {!isCollapsed && (
                <span className="ml-2 text-xs">{mod.label}</span>
              )}
            </Link>
          )}

          {hasChildren && isExpanded && !isCollapsed && (
            <div id={`${mod.key}-submenu`} className="space-y-1 px-5">
              {renderModules(mod.children ?? [], level + 1)}
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <>
      {/* Mobile toggle button */}
      <button
        className="md:hidden fixed top-4 left-4 z-50 bg-gray-900 text-white p-2 rounded-lg shadow-lg"
        onClick={toggleMobile}
        aria-label="Toggle Sidebar"
      >
        Toggle
      </button>

      {/* Hover wrapper for collapse/expand */}
      <div
        onMouseEnter={() => {
          if (!isFixed) {
            setIsCollapsed(false);
            setSidebarData({ isCollapsed: false });
          }
        }}
        onMouseLeave={() => {
          if (!isFixed) {
            setIsCollapsed(true);
            setSidebarData({ isCollapsed: true });
          }
        }}
      >
        <aside
          className={clsx(
            "fixed top-0 left-0 z-40 flex flex-col bg-[#2E2E2E] text-white border-r border-gray-700 transition-all duration-300 ease-in-out",
            {
              "w-[260px]": !isCollapsed,
              "w-15": isCollapsed,
              "translate-x-0": isMobileOpen,
              "-translate-x-full": !isMobileOpen,
              "md:translate-x-0 md:relative md:block": true,
            }
          )}
          style={{ height: "100vh", overflow: "hidden" }} // Prevent outer scroll
        >
          {/* Header */}

          {!isCollapsed && (
            <button
              onClick={() => setIsFixed(!isFixed)}
              className="rounded-full transform rotate-[90deg] absolute top-[5px] right-[10px] cursor-pointer hover:bg-gray-700 h-[max-content]"
            >
              <Pin className={`w-4 h-4 ${isFixed === true && "fill-white"}`} />
            </button>
          )}
          <div
            className={clsx(
              "flex items-center justify-between p-6 border-b border-gray-800 mt-6",
              {
                "justify-center": isCollapsed,
              }
            )}
            style={{ flexShrink: 0 }}
          >
            <span className="text-[16px] select-none flex items-center gap-2">
              <img
                src="/favicon.ico"
                alt="BookingCabs Logo"
                className="w-6 h-6"
              />
              {!isCollapsed && "BookingCabs"}
            </span>

            <button
              className="hidden md:inline-flex p-1 rounded hover:bg-gray-700 transition"
              onClick={toggleCollapse}
              aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {isCollapsed ? (
                <ChevronRight size={20} />
              ) : (
                <ChevronLeft size={20} />
              )}
            </button>
          </div>

          {/* Scrollable navigation */}
          <nav
            className={clsx(
              "p-4 space-y-1",
              !isCollapsed ? "overflow-y-auto" : "overflow-hidden"
            )}
            style={{
              height: "calc(100vh - 80px)",
              scrollbarWidth: isCollapsed ? "none" : "auto",
              msOverflowStyle: isCollapsed ? "none" : "auto",
            }}
          >
            {renderModules(filteredModules ?? [])}
          </nav>
        </aside>
      </div>
    </>
  );
}
